import { FC, useState } from "react";
import SearchCasesNew from "components/SearchCasesNew";
import RangePicker from "components/RangePicker";
import { IWorkspaceMember } from "dto/IWorkspace";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { ITag, TagType } from "dto/ITag";
import Chip from "components/Chip";
import { Avatar } from "@mui/material";
import { convertToLetters } from "utils/convertToLetters";
import styles from "./styles.module.scss";
import SearchLabel from "components/SearchLabel";
import SearchMemberNew from "components/SearchMemberNew";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Input from "components/Input";
import SelectFlagNew from "components/SelectFlagNew";
import { IStatus } from "dto/IStatus";
import StatusBadge from "components/StatusBadge";
import RichEditor from "components/RichEditor";
import { formatDate } from "utils/formatDate";
import { ListType } from "dto/ListType";
import SelectCalendar from "components/SelectCalendar";
import { ICalendar } from "dto/IBusyDate";

interface TaskFormProps {
  c: ICaseSimple | null;
  setCase: (cas: ICaseSimple | null) => void;
  content: string;
  setContent: (content: string) => void;
  members: IWorkspaceMember[];
  setMembers: (members: IWorkspaceMember[]) => void;
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  myPrivilages: any;
  tags: ITag[];
  setTags: (tags: ITag[]) => void;
  title: string;
  setTitle: (title: string) => void;

  setAllDay(state: boolean): void;
  allDay: boolean;
  calendar: ICalendar;
  setCalendar: (calendar: ICalendar) => void;
}

const TaskForm: FC<TaskFormProps> = ({
  c,
  setCase,
  content,
  setContent,
  members,
  setMembers,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  myPrivilages,
  tags,
  setTags,
  title,
  setTitle,
  setAllDay,
  allDay,
  calendar,
  setCalendar,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<IStatus | null>(null);
  return (
    <>
      <div className={styles.inputs}>
        <SearchCasesNew
          settedCase={c}
          setAddNewEntity={() => {}}
          onChoose={(cas) => setCase(cas)}
        />

        <Input value={title} onChange={(e) => setTitle(e)} label={t("forms.title")} />

        <div className={styles.controls}>
          <RangePicker
            defaultAllDay={false}
            defaultDates={
              startDate && endDate ? [startDate, endDate] : undefined
            }
            onChangeIsFullDay={(state) => {
              setAllDay(state);
            }}
            onAccept={(dateRange) => {
              setStartDate(dateRange[0]!.toDate());
              setEndDate(dateRange[1]!.toDate());
            }}
          />
          <SelectFlagNew
            selectedStatus={status}
            onChoose={(status) => setStatus(status)}
            listType={ListType.Tasks}
          />
          <SearchLabel
            label={t("common.label")}
            type={TagType.Tasks}
            onChoose={(tag) => {
              const isExist = tags.find((t) => t.Name === tag?.Name);
              if (isExist) return;
              setTags([...tags, tag!]);
            }}
          />

          {myPrivilages.canTaskAssign && (
            <SearchMemberNew
              onChoose={(member) => {
                setMembers([...members, member as IWorkspaceMember]);
              }}
            />
          )}

          <SelectCalendar
            selected={calendar}
            onChoose={(c) => setCalendar(c)}
          ></SelectCalendar>
        </div>
        <div className={styles.list}>
          {tags.map((tag) => (
            <Chip
              key={tag.Id}
              element={<>{tag.Name}</>}
              onDelete={() => {
                setTags(tags.filter((t) => t.Id !== tag.Id));
              }}
            />
          ))}
          {members.map((member) => (
            <Chip
              key={member.Id}
              avatar={
                <Avatar className={styles.avatar}>
                  {convertToLetters(member.Label)}
                </Avatar>
              }
              element={
                <div
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  {member.Label}
                </div>
              }
              onDelete={() => {
                setMembers(members.filter((f) => f.Id !== member.Id));
              }}
            />
          ))}
          {status && (
            <StatusBadge
              showName
              status={status}
              allowRemove
              onRemove={() => setStatus(null)}
            />
          )}
          {moment(startDate).isValid() && startDate && (
            <Chip
              element={<>{formatDate([startDate, endDate], allDay)}</>}
              onDelete={() => {
                setStartDate(undefined);
                setEndDate(undefined);
              }}
            />
          )}
        </div>

        <div className="h-[100%]">
          <RichEditor
            content={content}
            setContent={(text) => setContent(text)}
          />
        </div>
      </div>
    </>
  );
};

export default TaskForm;
